import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AttachFile, AdminPanelSettings } from '@mui/icons-material';
import { BRAND_COLORS } from '../../../styles/theme';
import { useFileHashBackfill } from '../hooks/useFileHashBackfill';

/**
 * File Hash Backfill admin tool. Calculates and stores file hashes for existing
 * flights and surfaces duplicate groups. Consumes useFileHashBackfill internally.
 */
export const FileHashBackfillCard: React.FC = () => {
  const fileHashBackfill = useFileHashBackfill();

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AttachFile sx={{ mr: 1, color: BRAND_COLORS.forestGreen.main }} />
          <Typography variant="h6" component="h2">
            File Hash Backfill
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Calculate and store file hashes for existing flights. Detects duplicate files.
        </Typography>

        {fileHashBackfill.backfillResult && (
          <Alert
            severity={fileHashBackfill.backfillResult.duplicate_count > 0 ? 'warning' : 'success'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              {fileHashBackfill.backfillResult.dry_run ? 'Preview: ' : ''}
              {fileHashBackfill.backfillResult.message}
            </Typography>
            {fileHashBackfill.backfillResult.duplicate_count > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Found {fileHashBackfill.backfillResult.duplicate_count} duplicate groups (
                {fileHashBackfill.backfillResult.duplicates_found.reduce(
                  (sum, d) => sum + d.count,
                  0
                )}{' '}
                files)
              </Typography>
            )}
          </Alert>
        )}

        {fileHashBackfill.backfillResult?.duplicates_found &&
          fileHashBackfill.backfillResult.duplicates_found.length > 0 && (
            <Box sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Duplicate Groups:
              </Typography>
              {fileHashBackfill.backfillResult.duplicates_found.map((group, idx) => (
                <Box
                  key={group.file_hash}
                  sx={{
                    mb: 2,
                    pl: 1,
                    borderLeft: '2px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Group {idx + 1} (hash: {group.file_hash})
                  </Typography>
                  {group.flights.map((flight) => (
                    <Typography
                      key={flight.flight_id}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {flight.s3_key}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          )}

        {/* Duplicates from Check Duplicates button */}
        {fileHashBackfill.duplicatesResult && (
          <Alert
            severity={fileHashBackfill.duplicatesResult.duplicate_count > 0 ? 'warning' : 'success'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              {fileHashBackfill.duplicatesResult.duplicate_count > 0
                ? `Found ${fileHashBackfill.duplicatesResult.duplicate_count} duplicate groups (${fileHashBackfill.duplicatesResult.total_duplicate_files} files)`
                : 'No duplicate files found'}
            </Typography>
          </Alert>
        )}

        {fileHashBackfill.duplicatesResult?.duplicates &&
          fileHashBackfill.duplicatesResult.duplicates.length > 0 && (
            <Box sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Duplicate Groups:
              </Typography>
              {fileHashBackfill.duplicatesResult.duplicates.map((group, idx) => (
                <Box
                  key={group.file_hash}
                  sx={{
                    mb: 2,
                    pl: 1,
                    borderLeft: '2px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Group {idx + 1} (hash: {group.file_hash.slice(0, 16)}...)
                  </Typography>
                  {group.flights.map((flight) => (
                    <Typography
                      key={flight.id}
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {flight.s3_key}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          )}

        <Chip
          icon={<AdminPanelSettings />}
          label="XER Admin Only"
          color="primary"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            variant="outlined"
            onClick={() => fileHashBackfill.runBackfill(true)}
            disabled={fileHashBackfill.isLoading}
            sx={{ flex: 1 }}
          >
            {fileHashBackfill.backfillLoading ? <CircularProgress size={20} /> : 'Preview Backfill'}
          </Button>
          <Button
            variant="contained"
            onClick={() => fileHashBackfill.runBackfill(false)}
            disabled={fileHashBackfill.isLoading}
            sx={{
              flex: 1,
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: BRAND_COLORS.forestGreen.main,
                opacity: 0.8,
              },
            }}
          >
            {fileHashBackfill.backfillLoading ? <CircularProgress size={20} /> : 'Run Backfill'}
          </Button>
        </Box>
        <Button
          fullWidth
          variant="outlined"
          color="warning"
          onClick={fileHashBackfill.checkDuplicates}
          disabled={fileHashBackfill.isLoading}
        >
          {fileHashBackfill.duplicatesLoading ? <CircularProgress size={20} /> : 'Check Duplicates'}
        </Button>
      </CardActions>
    </Card>
  );
};
